// src/features/products/pages/BarcodePrintPage.tsx
// ADVANCED BARCODE STUDIO & PRINT HUB — CLEAN LIGHT MODE & WYSIWYG PRESETS

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { searchProducts, ProductResult } from '@/features/pos/services/posApi';
import {
    ArrowLeft, Printer, Package, Barcode, Search, X,
    Plus, Minus, Trash2, Tag, DollarSign, Sliders,
    ZoomIn, ZoomOut, Eye, Settings2, Sparkles,
    Grid, Check, Copy, Store, ChevronRight, ChevronLeft, Layers,
    Maximize2, RefreshCw, HelpCircle, Info, FileText
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// OFFLINE CODE 128B BARCODE VECTOR GENERATOR
// Produces 100% standard vector SVG without third-party APIs
// ============================================================

function generateBarcodeSvg(data: string, height: number = 36, barMultiplier: number = 1.35): string {
    const clean = data.trim() || 'ETIPOS';
    const CODE128_PATTERNS = [
        "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
        "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
        "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
        "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
        "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
        "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
        "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
        "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
        "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
        "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
        "114131", "311141", "411131", "211412", "211214", "211232", "2331112"
    ];
    const START_B = 104;
    const STOP = 106;
    let checksum = START_B;
    const indices = [START_B];

    for (let i = 0; i < clean.length; i++) {
        let code = clean.charCodeAt(i) - 32;
        if (code < 0 || code > 95) code = 0;
        indices.push(code);
        checksum += code * (i + 1);
    }
    indices.push(checksum % 103);
    indices.push(STOP);

    let patternString = "";
    for (const idx of indices) {
        patternString += CODE128_PATTERNS[idx] || "";
    }

    let x = 6;
    let rects = "";
    for (let i = 0; i < patternString.length; i++) {
        const width = parseInt(patternString[i], 10) * barMultiplier;
        const isBar = i % 2 === 0;
        if (isBar) {
            rects += `<rect x="${x.toFixed(1)}" y="0" width="${width.toFixed(1)}" height="${height}" fill="#000" />`;
        }
        x += width;
    }
    const totalWidth = x + 6;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalWidth.toFixed(1)} ${height}" class="label-barcode-svg" style="width: 100%; height: ${height}px; display: block; margin: 0 auto;">${rects}</svg>`;
}

// ============================================================
// PRESET CONFIGURATIONS
// ============================================================

export type PresetKey =
    | 'thermal_50x30'
    | 'shelf_70x40'
    | 'shelf_price_card'
    | 'compact_38x25'
    | 'a4_sheet'
    | 'a4_40up'
    | 'a4_14up_shelf'
    | 'a4_16up_shelf';

export interface LabelPreset {
    id: PresetKey;
    name: string;
    dimensions: string;
    widthMm: number;
    heightMm: number;
    paperType: 'roll' | 'sheet';
    badge: string;
    description: string;
    barcodeHeight: number;
}

const PRESETS: Record<PresetKey, LabelPreset> = {
    thermal_50x30: {
        id: 'thermal_50x30',
        name: 'Standard Thermal Roll',
        dimensions: '50mm × 30mm',
        widthMm: 50,
        heightMm: 30,
        paperType: 'roll',
        badge: 'Recommended',
        description: 'Standard 2" × 1.2" sticker roll for Xprinter, Zebra, Dymo',
        barcodeHeight: 32,
    },
    shelf_70x40: {
        id: 'shelf_70x40',
        name: 'Retail Shelf Talker',
        dimensions: '70mm × 40mm',
        widthMm: 70,
        heightMm: 40,
        paperType: 'roll',
        badge: 'High Impact',
        description: 'Large retail price banner tag for supermarket gondolas',
        barcodeHeight: 38,
    },
    shelf_price_card: {
        id: 'shelf_price_card',
        name: 'Promo Shelf Card (80×50)',
        dimensions: '80mm × 50mm',
        widthMm: 80,
        heightMm: 50,
        paperType: 'roll',
        badge: 'Bold Promo',
        description: 'Prominent supermarket shelf card with jumbo price display and barcode',
        barcodeHeight: 44,
    },
    compact_38x25: {
        id: 'compact_38x25',
        name: 'Compact Jewelry & Pharma',
        dimensions: '38mm × 25mm',
        widthMm: 38,
        heightMm: 25,
        paperType: 'roll',
        badge: 'Mini',
        description: 'Mini label for cosmetics, jewelry, and small bottles',
        barcodeHeight: 26,
    },
    a4_sheet: {
        id: 'a4_sheet',
        name: 'A4 Sheet (24-up Grid)',
        dimensions: '63.5mm × 33.9mm',
        widthMm: 63.5,
        heightMm: 33.9,
        paperType: 'sheet',
        badge: 'Standard Sheet',
        description: '3 cols × 8 rows (24 labels per sheet) on standard A4 paper',
        barcodeHeight: 30,
    },
    a4_40up: {
        id: 'a4_40up',
        name: 'A4 Sheet (40-up Micro-Tags)',
        dimensions: '48.5mm × 25.4mm',
        widthMm: 48.5,
        heightMm: 25.4,
        paperType: 'sheet',
        badge: 'High Density',
        description: '4 cols × 10 rows (40 compact labels per A4 sheet) for small items',
        barcodeHeight: 24,
    },
    a4_14up_shelf: {
        id: 'a4_14up_shelf',
        name: 'A4 Shelf Talker (14-up Large)',
        dimensions: '105mm × 42.4mm',
        widthMm: 105,
        heightMm: 42.4,
        paperType: 'sheet',
        badge: 'Shelf Edge',
        description: '2 cols × 7 rows (14 prominent price cards per sheet) for store shelves',
        barcodeHeight: 36,
    },
    a4_16up_shelf: {
        id: 'a4_16up_shelf',
        name: 'A4 Shelf Edge (16-up Grid)',
        dimensions: '105mm × 37.1mm',
        widthMm: 105,
        heightMm: 37.1,
        paperType: 'sheet',
        badge: 'Gondola Edge',
        description: '2 cols × 8 rows (16 price channel tags per sheet) for retail fixtures',
        barcodeHeight: 32,
    },
};

interface QueueItem {
    product: ProductResult;
    copies: number;
}

export function BarcodePrintPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const tenant = useAuthStore((s) => s.tenant);
    const addToast = useToastStore((s) => s.addToast);

    // Search state
    const [search, setSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    // Queue state: per-product copies
    const [queue, setQueue] = useState<QueueItem[]>([]);

    // Customization & Presets
    const [presetKey, setPresetKey] = useState<PresetKey>('thermal_50x30');
    const [storeName, setStoreName] = useState(tenant?.name || 'EtiPOS Retail');
    const [showStoreName, setShowStoreName] = useState(true);
    const [showPrice, setShowPrice] = useState(true);
    const [showSku, setShowSku] = useState(true);
    const [showBarcodeText, setShowBarcodeText] = useState(true);
    const [borderStyle, setBorderStyle] = useState<'dashed' | 'solid' | 'none'>('dashed');

    // Studio View state
    const [activeTab, setActiveTab] = useState<'studio' | 'queue'>('studio');
    const [previewIndex, setPreviewIndex] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1.4);
    const [showPrintTips, setShowPrintTips] = useState(false);
    const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
    const [sheetViewMode, setSheetViewMode] = useState<'sheet' | 'single'>('sheet');

    // Query products for search
    const { data: searchResults, isLoading: isSearching } = useQuery({
        queryKey: ['barcode-print-search', user?.tenantId, search],
        queryFn: () => searchProducts(user!.tenantId, search || undefined),
        enabled: !!user?.tenantId && search.length >= 2,
    });

    const activePreset = PRESETS[presetKey];

    // Compute totals
    const totalUniqueProducts = queue.length;
    const totalLabelsCount = useMemo(() => {
        return queue.reduce((sum, item) => sum + (item.copies || 1), 0);
    }, [queue]);

    // Add product to queue
    const handleAddProduct = (product: ProductResult) => {
        const existing = queue.find(q => q.product.id === product.id);
        if (existing) {
            setQueue(queue.map(q => q.product.id === product.id ? { ...q, copies: q.copies + 1 } : q));
            addToast({ type: 'info', title: 'Incremented copies', message: `Added another label for ${product.name}` });
        } else {
            setQueue(prev => [...prev, { product, copies: 1 }]);
            addToast({ type: 'success', title: 'Added to print queue', message: product.name });
        }
        setSearch('');
        setShowDropdown(false);
    };

    // Bulk add products from modal
    const handleBulkAddProducts = (itemsToAdd: Array<{ product: ProductResult; copies: number }>) => {
        if (!itemsToAdd || itemsToAdd.length === 0) return;
        setQueue(prev => {
            const next = [...prev];
            for (const item of itemsToAdd) {
                const existingIdx = next.findIndex(q => q.product.id === item.product.id);
                if (existingIdx >= 0) {
                    next[existingIdx] = { ...next[existingIdx], copies: next[existingIdx].copies + item.copies };
                } else {
                    next.push({ product: item.product, copies: item.copies });
                }
            }
            return next;
        });
        addToast({
            type: 'success',
            title: 'Products added to queue',
            message: `Added ${itemsToAdd.length} products to print queue.`,
        });
        setShowMultiSelectModal(false);
    };

    // Load sample products for quick testing
    const handleLoadDemoProducts = () => {
        const demos: ProductResult[] = [
            { id: 'demo-1', name: 'Indomie Chicken Noodles 70g', slug: 'indomie-chicken-70g', sku: 'IND-CHK-70G', barcode: '899886620011', retailPrice: '250.00' },
            { id: 'demo-2', name: 'Golden Penny Spaghetti 500g', slug: 'golden-penny-spaghetti-500g', sku: 'GP-SPAG-500', barcode: '779886620022', retailPrice: '850.00' },
            { id: 'demo-3', name: 'Peak Evaporated Milk 160g', slug: 'peak-evaporated-milk-160g', sku: 'PEAK-MILK-160', barcode: '899886620033', retailPrice: '600.00' },
        ];
        demos.forEach(d => {
            if (!queue.some(q => q.product.id === d.id)) {
                setQueue(prev => [...prev, { product: d, copies: 2 }]);
            }
        });
        addToast({ type: 'success', title: 'Sample Products Loaded', message: 'Loaded 3 products with 2 copies each for instant preview.' });
    };

    // Update copies for specific product
    const handleUpdateCopies = (productId: string, delta: number) => {
        setQueue(prev => prev.map(item => {
            if (item.product.id === productId) {
                const newCopies = Math.max(1, Math.min(500, item.copies + delta));
                return { ...item, copies: newCopies };
            }
            return item;
        }));
    };

    // Set copies directly
    const handleSetCopiesDirect = (productId: string, count: number) => {
        const safeCount = Math.max(1, Math.min(500, count || 1));
        setQueue(prev => prev.map(item => item.product.id === productId ? { ...item, copies: safeCount } : item));
    };

    // Remove from queue
    const handleRemoveProduct = (productId: string) => {
        setQueue(prev => prev.filter(item => item.product.id !== productId));
        if (previewIndex >= queue.length - 1) {
            setPreviewIndex(Math.max(0, queue.length - 2));
        }
    };

    // Bulk set all copies
    const handleApplyCopiesToAll = (count: number) => {
        setQueue(prev => prev.map(item => ({ ...item, copies: count })));
        addToast({ type: 'info', title: 'Updated all items', message: `All products set to ${count} copies each` });
    };

    // Clear all
    const handleClearAll = () => {
        setQueue([]);
        setPreviewIndex(0);
    };

    // Active product for WYSIWYG preview
    const previewProduct: ProductResult | null = useMemo(() => {
        if (queue.length === 0) return null;
        const safeIdx = Math.min(previewIndex, queue.length - 1);
        return queue[safeIdx]?.product || null;
    }, [queue, previewIndex]);

    // Flattened labels stream across all queued items
    const flattenedLabels = useMemo(() => {
        return queue.flatMap(item => {
            const prod = item.product;
            const barcode = prod.barcode || prod.sku || prod.id.substring(0, 10);
            const price = prod.retailPrice ? parseFloat(prod.retailPrice).toLocaleString() : '';
            const barcodeSvg = generateBarcodeSvg(barcode, activePreset.barcodeHeight);

            return Array(item.copies).fill({
                name: prod.name || 'Product',
                sku: prod.sku,
                barcode,
                price,
                svg: barcodeSvg,
            });
        });
    }, [queue, activePreset.barcodeHeight]);

    // Capacity per A4 sheet
    const labelsPerPage = useMemo(() => {
        if (activePreset.id === 'a4_40up') return 40;
        if (activePreset.id === 'a4_14up_shelf') return 14;
        if (activePreset.id === 'a4_16up_shelf') return 16;
        if (activePreset.id === 'a4_sheet') return 24;
        return 1;
    }, [activePreset.id]);

    // ============================================================
    // PRINT GENERATION ENGINE
    // ============================================================

    const handlePrint = () => {
        if (queue.length === 0) return;

        const printWindow = window.open('', '_blank', 'width=980,height=750');
        if (!printWindow) {
            addToast({ type: 'error', title: 'Popup Blocked', message: 'Please allow popups to open the print dialog.' });
            return;
        }

        const isSheet = activePreset.paperType === 'sheet';

        let bodyHtml = '';

        if (isSheet) {
            // Group flattened labels into A4 sheets
            const sheets: Array<typeof flattenedLabels> = [];
            for (let i = 0; i < flattenedLabels.length; i += labelsPerPage) {
                sheets.push(flattenedLabels.slice(i, i + labelsPerPage));
            }

            bodyHtml = sheets.map((sheetLabels) => {
                const labelsMarkup = sheetLabels.map((lbl) => `
                    <div class="print-label">
                        ${showStoreName && storeName ? `<div class="lbl-store">${storeName}</div>` : ''}
                        <div class="lbl-name">${lbl.name}</div>
                        ${showSku && lbl.sku ? `<div class="lbl-sku">SKU: ${lbl.sku}</div>` : ''}
                        <div class="lbl-barcode">${lbl.svg}</div>
                        ${showBarcodeText ? `<div class="lbl-code">${lbl.barcode}</div>` : ''}
                        ${showPrice && lbl.price ? `<div class="lbl-price">₦${lbl.price}</div>` : ''}
                    </div>
                `).join('');

                const emptyCount = labelsPerPage - sheetLabels.length;
                const emptyMarkup = emptyCount > 0
                    ? Array(emptyCount).fill('<div class="print-label print-label-empty"></div>').join('')
                    : '';

                return `
                    <div class="a4-sheet-container">
                        ${labelsMarkup}
                        ${emptyMarkup}
                    </div>
                `;
            }).join('');
        } else {
            bodyHtml = flattenedLabels.map((lbl) => `
                <div class="print-label">
                    ${showStoreName && storeName ? `<div class="lbl-store">${storeName}</div>` : ''}
                    <div class="lbl-name">${lbl.name}</div>
                    ${showSku && lbl.sku ? `<div class="lbl-sku">SKU: ${lbl.sku}</div>` : ''}
                    <div class="lbl-barcode">${lbl.svg}</div>
                    ${showBarcodeText ? `<div class="lbl-code">${lbl.barcode}</div>` : ''}
                    ${showPrice && lbl.price ? `<div class="lbl-price">₦${lbl.price}</div>` : ''}
                </div>
            `).join('');
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Print Labels - ${storeName || 'EtiPOS'}</title>
                <style>
                    @page {
                        ${isSheet ? `
                            size: A4 portrait;
                            margin: ${activePreset.id === 'a4_14up_shelf' || activePreset.id === 'a4_16up_shelf' ? '0mm' : '8mm 6mm'};
                        ` : `
                            size: ${activePreset.widthMm}mm ${activePreset.heightMm}mm;
                            margin: 0mm;
                        `}
                    }
                    * {
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        background: #ffffff;
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                    }
                    ${isSheet ? `
                        .a4-sheet-container {
                            width: 100%;
                            max-width: ${activePreset.id === 'a4_14up_shelf' || activePreset.id === 'a4_16up_shelf' ? '210mm' : '198mm'};
                            height: ${activePreset.id === 'a4_14up_shelf' || activePreset.id === 'a4_16up_shelf' ? '297mm' : '281mm'};
                            display: grid;
                            grid-template-columns: ${
                                activePreset.id === 'a4_40up' ? 'repeat(4, 48.5mm)' :
                                activePreset.id === 'a4_14up_shelf' || activePreset.id === 'a4_16up_shelf' ? 'repeat(2, 105mm)' :
                                'repeat(3, 63.5mm)'
                            };
                            grid-template-rows: ${
                                activePreset.id === 'a4_40up' ? 'repeat(10, 25.4mm)' :
                                activePreset.id === 'a4_14up_shelf' ? 'repeat(7, 42.4mm)' :
                                activePreset.id === 'a4_16up_shelf' ? 'repeat(8, 37.1mm)' :
                                'repeat(8, 33.9mm)'
                            };
                            gap: 0mm;
                            justify-content: center;
                            page-break-after: always;
                            break-after: page;
                            margin: 0 auto;
                        }
                        .a4-sheet-container:last-child {
                            page-break-after: auto;
                            break-after: auto;
                        }
                        .print-label-empty {
                            border: none !important;
                            background: transparent !important;
                        }
                    ` : ''}
                    .print-label {
                        width: ${activePreset.widthMm}mm;
                        height: ${activePreset.heightMm}mm;
                        padding: ${
                            activePreset.id === 'compact_38x25' || activePreset.id === 'a4_40up'
                                ? '1.5mm 2mm'
                                : activePreset.id.includes('shelf') || activePreset.id === 'shelf_price_card'
                                ? '3mm 4mm'
                                : '2mm 3mm'
                        };
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        align-items: center;
                        text-align: center;
                        background: #ffffff;
                        overflow: hidden;
                        page-break-inside: avoid;
                        ${isSheet ? '' : 'page-break-after: always;'}
                        ${borderStyle === 'dashed' ? 'border: 0.15mm dashed #cbd5e1;' : borderStyle === 'solid' ? 'border: 0.2mm solid #1e293b;' : 'border: none;'}
                    }
                    .lbl-store {
                        font-size: ${activePreset.id === 'compact_38x25' || activePreset.id === 'a4_40up' ? '7px' : '9px'};
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #64748b;
                        line-height: 1;
                        margin-bottom: 1px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        width: 100%;
                    }
                    .lbl-name {
                        font-size: ${
                            activePreset.id === 'compact_38x25' || activePreset.id === 'a4_40up'
                                ? '8.5px'
                                : activePreset.id === 'shelf_price_card' || activePreset.id === 'shelf_70x40' || activePreset.id === 'a4_14up_shelf'
                                ? '13px'
                                : '11px'
                        };
                        font-weight: 700;
                        color: #0f172a;
                        line-height: 1.15;
                        max-height: 2.3em;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        width: 100%;
                    }
                    .lbl-sku {
                        font-size: 8px;
                        color: #94a3b8;
                        font-family: monospace;
                        line-height: 1;
                    }
                    .lbl-barcode {
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 1px 0;
                    }
                    .lbl-barcode svg {
                        max-width: 95%;
                        height: ${activePreset.barcodeHeight}px;
                    }
                    .lbl-code {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: ${activePreset.id === 'compact_38x25' || activePreset.id === 'a4_40up' ? '7.5px' : '9.5px'};
                        font-weight: 600;
                        letter-spacing: 1px;
                        color: #334155;
                        line-height: 1;
                    }
                    .lbl-price {
                        font-size: ${
                            activePreset.id === 'shelf_price_card'
                                ? '24px'
                                : activePreset.id === 'shelf_70x40' || activePreset.id === 'a4_14up_shelf'
                                ? '19px'
                                : activePreset.id === 'a4_16up_shelf'
                                ? '16px'
                                : activePreset.id === 'compact_38x25' || activePreset.id === 'a4_40up'
                                ? '10px'
                                : '13px'
                        };
                        font-weight: 900;
                        color: #047857;
                        line-height: 1;
                        margin-top: 1px;
                    }
                    @media print {
                        body { background: transparent; }
                    }
                </style>
            </head>
            <body>
                ${bodyHtml}
            </body>
            </html>
        `);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 500);
    };

    return (
        <div className="min-h-screen bg-slate-50/70">
            {/* Top Navigation Bar */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/products')}
                            className="p-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 transition-colors"
                            type="button"
                            title="Back to Catalog"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                    <Barcode className="w-5 h-5 text-emerald-600" />
                                    Barcode Print Studio
                                </h1>
                                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Vector Code-128
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Design, preview, and print labels tailored for thermal roll printers and A4 sticker sheets
                            </p>
                        </div>
                    </div>

                    {/* Quick Stats & Print Button */}
                    <div className="flex items-center gap-3">
                        {queue.length > 0 && (
                            <div className="hidden sm:flex items-center gap-3 bg-slate-100 px-3.5 py-1.5 rounded-xl border border-slate-200 text-xs text-slate-600">
                                <span><strong className="text-slate-800 font-bold">{totalUniqueProducts}</strong> products</span>
                                <span className="text-slate-300">•</span>
                                <span><strong className="text-emerald-700 font-bold">{totalLabelsCount}</strong> total labels</span>
                            </div>
                        )}

                        {queue.length > 0 && (
                            <button
                                onClick={handleClearAll}
                                className="px-3 py-2 text-xs font-semibold text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                                type="button"
                            >
                                Clear All
                            </button>
                        )}

                        <Button
                            onClick={handlePrint}
                            disabled={queue.length === 0}
                            leftIcon={<Printer size={16} />}
                            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded-xl shadow-sm"
                        >
                            Print Labels {totalLabelsCount > 0 ? `(${totalLabelsCount})` : ''}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Studio Body */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* ============================================================ */}
                    {/* LEFT PANEL: CONFIGURATION & QUEUE (COL 1-5)                   */}
                    {/* ============================================================ */}
                    <div className="lg:col-span-5 space-y-5">

                        {/* PRODUCT SEARCH & ADD */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 relative">
                            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2.5 flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <Search className="w-3.5 h-3.5 text-emerald-600" />
                                    Find & Add Products
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setShowMultiSelectModal(true)}
                                        className="text-[11px] text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 font-bold px-2 py-0.5 rounded-lg transition-colors flex items-center gap-1 shadow-2xs"
                                        type="button"
                                    >
                                        <Layers className="w-3 h-3 text-emerald-600" />
                                        Select Multiple
                                    </button>
                                    {queue.length === 0 && (
                                        <button
                                            onClick={handleLoadDemoProducts}
                                            className="text-[11px] text-emerald-600 font-semibold hover:underline"
                                            type="button"
                                        >
                                            + Sample Items
                                        </button>
                                    )}
                                </div>
                            </label>

                            <div className="relative">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Type product name or barcode..."
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                                    onFocus={() => setShowDropdown(true)}
                                    className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all"
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {isSearching && <Spinner size="xs" variant="emerald" />}
                                    {search && !isSearching && (
                                        <button onClick={() => { setSearch(''); setShowDropdown(false); }} className="p-1 rounded-lg hover:bg-slate-200 text-slate-400">
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Autocomplete Dropdown */}
                            <AnimatePresence>
                                {showDropdown && search.length >= 2 && searchResults && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        className="absolute left-5 right-5 z-20 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-64 overflow-y-auto divide-y divide-slate-100"
                                    >
                                        {searchResults.length === 0 ? (
                                            <div className="text-center py-6">
                                                <Package size={28} className="text-slate-300 mx-auto mb-1.5" />
                                                <p className="text-xs text-slate-500">No products match your search</p>
                                            </div>
                                        ) : (
                                            searchResults.slice(0, 10).map((prod) => {
                                                const inQueue = queue.some(q => q.product.id === prod.id);
                                                return (
                                                    <button
                                                        key={prod.id}
                                                        onClick={() => handleAddProduct(prod)}
                                                        className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-emerald-50/60 transition-colors group cursor-pointer"
                                                        type="button"
                                                    >
                                                        <div className="min-w-0 pr-3">
                                                            <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-emerald-700">
                                                                {prod.name}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <code className="text-[10px] text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                                                                    {prod.barcode || prod.sku || 'No code'}
                                                                </code>
                                                                {prod.retailPrice && (
                                                                    <span className="text-xs font-bold text-emerald-600">
                                                                        ₦{parseFloat(prod.retailPrice).toLocaleString()}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                                            {inQueue ? (
                                                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                                    <Check className="w-3 h-3" /> In Queue
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1">
                                                                    <Plus className="w-3.5 h-3.5" /> Add
                                                                </span>
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* PRESET PICKER */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
                            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                                <Layers className="w-3.5 h-3.5 text-emerald-600" />
                                Label Format & Paper Preset
                            </label>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                {(Object.keys(PRESETS) as PresetKey[]).map((key) => {
                                    const p = PRESETS[key];
                                    const isSelected = presetKey === key;
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => setPresetKey(key)}
                                            type="button"
                                            className={clsx(
                                                'p-3.5 rounded-xl border-2 text-left transition-all relative overflow-hidden flex flex-col justify-between',
                                                isSelected
                                                    ? 'border-emerald-500 bg-emerald-50/50 shadow-xs'
                                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                            )}
                                        >
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={clsx('text-xs font-bold', isSelected ? 'text-emerald-900' : 'text-slate-800')}>
                                                        {p.name}
                                                    </span>
                                                    <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded', isSelected ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-100 text-slate-500')}>
                                                        {p.badge}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] font-mono font-semibold text-emerald-700">{p.dimensions}</p>
                                                <p className="text-[10px] text-slate-400 mt-1 line-clamp-1">{p.description}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* CUSTOMIZATION TOGGLES */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
                            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                                <Sliders className="w-3.5 h-3.5 text-emerald-600" />
                                Label Elements
                            </label>

                            {/* Store Header Input */}
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                                        <Store className="w-3.5 h-3.5 text-slate-400" /> Business Header
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={showStoreName}
                                        onChange={(e) => setShowStoreName(e.target.checked)}
                                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                    />
                                </div>
                                {showStoreName && (
                                    <input
                                        type="text"
                                        value={storeName}
                                        onChange={(e) => setStoreName(e.target.value)}
                                        placeholder="Business Name..."
                                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium focus:ring-1 focus:ring-emerald-500"
                                    />
                                )}
                            </div>

                            {/* Element Switches */}
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                    <span className="font-medium text-slate-700">Retail Price</span>
                                    <input
                                        type="checkbox"
                                        checked={showPrice}
                                        onChange={(e) => setShowPrice(e.target.checked)}
                                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                    />
                                </label>

                                <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                    <span className="font-medium text-slate-700">SKU Code</span>
                                    <input
                                        type="checkbox"
                                        checked={showSku}
                                        onChange={(e) => setShowSku(e.target.checked)}
                                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                    />
                                </label>

                                <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                    <span className="font-medium text-slate-700">Barcode Digits</span>
                                    <input
                                        type="checkbox"
                                        checked={showBarcodeText}
                                        onChange={(e) => setShowBarcodeText(e.target.checked)}
                                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                    />
                                </label>

                                <div className="p-2.5 rounded-xl border border-slate-200 flex flex-col justify-between">
                                    <span className="font-medium text-slate-700 text-[11px] mb-1">Border Guide</span>
                                    <select
                                        value={borderStyle}
                                        onChange={(e) => setBorderStyle(e.target.value as any)}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg text-[11px] py-1 px-1.5 focus:ring-1 focus:ring-emerald-500"
                                    >
                                        <option value="dashed">Dashed Guide</option>
                                        <option value="solid">Solid Box</option>
                                        <option value="none">Borderless</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* COPIES TOOLBAR (LIGHT MODE) */}
                        {queue.length > 0 && (
                            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-slate-700">Quick Copies for All</p>
                                    <p className="text-[11px] text-slate-400">Apply count across all {queue.length} items</p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {[1, 2, 5, 10, 20].map((num) => (
                                        <button
                                            key={num}
                                            onClick={() => handleApplyCopiesToAll(num)}
                                            className="px-2.5 py-1 bg-slate-100 hover:bg-emerald-600 hover:text-white rounded-lg text-xs font-bold text-slate-700 transition-colors"
                                            type="button"
                                        >
                                            {num}x
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* PRINTER CALIBRATION & SETUP GUIDE (ACCORDION) */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                            <button
                                onClick={() => setShowPrintTips(!showPrintTips)}
                                className="w-full flex items-center justify-between text-left text-xs font-bold text-slate-700"
                                type="button"
                            >
                                <span className="flex items-center gap-1.5 text-emerald-700">
                                    <Info className="w-4 h-4" /> Thermal Printer Setup Tips
                                </span>
                                <span className="text-[11px] text-slate-400">{showPrintTips ? 'Hide' : 'Show Guide'}</span>
                            </button>

                            {showPrintTips && (
                                <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                                    <p>• <strong>Margins:</strong> In the browser print dialog, set Margins to <em>None</em>.</p>
                                    <p>• <strong>Paper Size:</strong> Choose user-defined size matching your label roll (e.g. 50×30mm).</p>
                                    <p>• <strong>Scale:</strong> Set Scale to <em>100% (Actual Size)</em> to ensure scannable, crisp vector bars.</p>
                                    <p>• <strong>Headers/Footers:</strong> Uncheck "Headers and Footers" to prevent browser URLs printing on your labels.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ============================================================ */}
                    {/* RIGHT PANEL: LIVE STUDIO CANVAS & PRINT QUEUE (COL 6-12)     */}
                    {/* ============================================================ */}
                    <div className="lg:col-span-7 space-y-5">

                        {/* Top View Switcher Tabs */}
                        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-1.5 shadow-sm">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setActiveTab('studio')}
                                    className={clsx(
                                        'flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all',
                                        activeTab === 'studio'
                                            ? 'bg-emerald-600 text-white shadow-xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                    )}
                                    type="button"
                                >
                                    <Eye className="w-4 h-4" />
                                    WYSIWYG Studio Canvas
                                </button>
                                <button
                                    onClick={() => setActiveTab('queue')}
                                    className={clsx(
                                        'flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all',
                                        activeTab === 'queue'
                                            ? 'bg-emerald-600 text-white shadow-xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                    )}
                                    type="button"
                                >
                                    <Grid className="w-4 h-4" />
                                    Print Queue ({queue.length})
                                </button>
                            </div>

                            {/* Canvas Zoom Slider */}
                            {activeTab === 'studio' && (
                                <div className="flex items-center gap-2 pr-3">
                                    <button
                                        onClick={() => setZoomLevel(Math.max(1, zoomLevel - 0.2))}
                                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                                        type="button"
                                        title="Zoom Out"
                                    >
                                        <ZoomOut className="w-3.5 h-3.5" />
                                    </button>
                                    <span className="text-[11px] font-mono text-slate-600 font-semibold w-10 text-center">
                                        {Math.round(zoomLevel * 100)}%
                                    </span>
                                    <button
                                        onClick={() => setZoomLevel(Math.min(2.5, zoomLevel + 0.2))}
                                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                                        type="button"
                                        title="Zoom In"
                                    >
                                        <ZoomIn className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* TAB 1: WYSIWYG STUDIO CANVAS (CLEAN LIGHT DRAFTING TABLE) */}
                        {activeTab === 'studio' && (
                            <div className="bg-slate-100/90 rounded-3xl p-8 border-2 border-slate-200/80 shadow-inner relative overflow-hidden flex flex-col items-center justify-center min-h-[480px]">
                                {/* Subtle Drafting Dot Grid Pattern */}
                                <div
                                    className="absolute inset-0 opacity-40 pointer-events-none"
                                    style={{
                                        backgroundImage: 'radial-gradient(circle, #cbd5e1 1.25px, transparent 1.25px)',
                                        backgroundSize: '18px 18px',
                                    }}
                                />

                                {/* Canvas Header Pill */}
                                <div className="absolute top-4 left-5 right-5 flex items-center justify-between text-[11px] text-slate-600 pointer-events-none">
                                    <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-slate-200 px-3 py-1.5 rounded-xl shadow-xs">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                        <span>Preset: <strong className="text-slate-800">{activePreset.name}</strong></span>
                                        <span className="text-slate-300">•</span>
                                        <span className="font-mono text-emerald-700 font-bold">{activePreset.dimensions}</span>
                                    </div>

                                    {/* View Mode Toggle for Sheet Presets */}
                                    {activePreset.paperType === 'sheet' && queue.length > 0 && (
                                        <div className="pointer-events-auto flex items-center bg-white border border-slate-200 rounded-xl p-0.5 shadow-xs">
                                            <button
                                                type="button"
                                                onClick={() => setSheetViewMode('sheet')}
                                                className={clsx(
                                                    'px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors',
                                                    sheetViewMode === 'sheet' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'
                                                )}
                                            >
                                                A4 Sheet View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSheetViewMode('single')}
                                                className={clsx(
                                                    'px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors',
                                                    sheetViewMode === 'single' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'
                                                )}
                                            >
                                                Single Label
                                            </button>
                                        </div>
                                    )}

                                    {/* Item Carousel Switcher */}
                                    {previewProduct && queue.length > 1 && (sheetViewMode === 'single' || activePreset.paperType === 'roll') && (
                                        <div className="pointer-events-auto flex items-center gap-1.5 bg-white border border-slate-200 px-2.5 py-1 rounded-xl shadow-xs text-xs text-slate-700">
                                            <button
                                                onClick={() => setPreviewIndex((previewIndex - 1 + queue.length) % queue.length)}
                                                className="p-0.5 rounded hover:bg-slate-100 text-slate-500"
                                                type="button"
                                                title="Previous Item"
                                            >
                                                <ChevronLeft className="w-3.5 h-3.5" />
                                            </button>
                                            <span className="font-semibold text-slate-800 px-1">
                                                {previewIndex + 1} of {queue.length}
                                            </span>
                                            <button
                                                onClick={() => setPreviewIndex((previewIndex + 1) % queue.length)}
                                                className="p-0.5 rounded hover:bg-slate-100 text-slate-500"
                                                type="button"
                                                title="Next Item"
                                            >
                                                <ChevronRight className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* A4 SHEET MULTI-PRODUCT GRID PREVIEW */}
                                {activePreset.paperType === 'sheet' && sheetViewMode === 'sheet' && queue.length > 0 ? (
                                    <div className="flex flex-col items-center">
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.96 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="bg-white border-2 border-slate-300 rounded-lg shadow-2xl p-3 w-[360px] sm:w-[410px] aspect-[1/1.414] overflow-hidden flex flex-col justify-between"
                                        >
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center border-b border-slate-100 pb-1 mb-1">
                                                {storeName || 'EtiPOS'} — A4 Paper Preview ({activePreset.name})
                                            </div>
                                            <div
                                                className="grid gap-1 flex-1 h-full"
                                                style={{
                                                    gridTemplateColumns: activePreset.id === 'a4_40up'
                                                        ? 'repeat(4, minmax(0, 1fr))'
                                                        : activePreset.id === 'a4_14up_shelf' || activePreset.id === 'a4_16up_shelf'
                                                        ? 'repeat(2, minmax(0, 1fr))'
                                                        : 'repeat(3, minmax(0, 1fr))',
                                                    gridTemplateRows: activePreset.id === 'a4_40up'
                                                        ? 'repeat(10, minmax(0, 1fr))'
                                                        : activePreset.id === 'a4_14up_shelf'
                                                        ? 'repeat(7, minmax(0, 1fr))'
                                                        : activePreset.id === 'a4_16up_shelf'
                                                        ? 'repeat(8, minmax(0, 1fr))'
                                                        : 'repeat(8, minmax(0, 1fr))',
                                                }}
                                            >
                                                {Array.from({ length: labelsPerPage }).map((_, slotIdx) => {
                                                    const lbl = flattenedLabels[slotIdx];
                                                    if (!lbl) {
                                                        return (
                                                            <div
                                                                key={`empty-${slotIdx}`}
                                                                className="border border-dashed border-slate-200 rounded flex items-center justify-center text-[8px] text-slate-300"
                                                            >
                                                                Empty
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <div
                                                            key={`slot-${slotIdx}`}
                                                            className={clsx(
                                                                'rounded p-1 flex flex-col justify-between text-center overflow-hidden bg-slate-50/50',
                                                                borderStyle === 'dashed' ? 'border border-dashed border-slate-300' : borderStyle === 'solid' ? 'border border-slate-800' : 'border border-transparent'
                                                            )}
                                                        >
                                                            <p className="text-[8px] font-bold text-slate-800 truncate leading-tight">
                                                                {lbl.name}
                                                            </p>
                                                            <div
                                                                className="w-full flex items-center justify-center overflow-hidden my-0.5"
                                                                dangerouslySetInnerHTML={{ __html: lbl.svg }}
                                                            />
                                                            <div className="flex items-center justify-between text-[7px] font-mono text-slate-500 px-0.5">
                                                                <span className="truncate">{lbl.barcode}</span>
                                                                {showPrice && lbl.price && (
                                                                    <span className="font-bold text-emerald-700">₦{lbl.price}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                        <div className="mt-3 flex items-center gap-3 text-xs text-slate-600 bg-white/90 border border-slate-200 px-3 py-1.5 rounded-xl shadow-2xs">
                                            <span className="font-semibold text-emerald-700">
                                                {Math.min(labelsPerPage, flattenedLabels.length)} of {labelsPerPage} spots filled on Sheet 1
                                            </span>
                                            <span>•</span>
                                            <span>{queue.length} distinct products</span>
                                            {flattenedLabels.length > labelsPerPage && (
                                                <>
                                                    <span>•</span>
                                                    <span className="text-amber-600 font-bold">
                                                        Total {Math.ceil(flattenedLabels.length / labelsPerPage)} A4 sheets
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : previewProduct ? (
                                    /* PHYSICAL LABEL PREVIEW CARD (LIGHT MODE) */
                                    <motion.div
                                        key={`${previewProduct.id}-${presetKey}`}
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.2 }}
                                        style={{
                                            width: `${activePreset.widthMm * 3.7795 * zoomLevel}px`,
                                            minHeight: `${activePreset.heightMm * 3.7795 * zoomLevel}px`,
                                            transformOrigin: 'center center',
                                        }}
                                        className={clsx(
                                            'bg-white text-slate-900 rounded-lg shadow-xl shadow-slate-300/40 p-4 flex flex-col justify-between items-center text-center transition-all relative select-none',
                                            borderStyle === 'dashed' ? 'border-2 border-dashed border-slate-300' : borderStyle === 'solid' ? 'border-2 border-slate-900' : 'border border-slate-200/60'
                                        )}
                                    >
                                        {/* Store Header */}
                                        {showStoreName && storeName && (
                                            <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-1 truncate w-full">
                                                {storeName}
                                            </p>
                                        )}

                                        {/* Product Name */}
                                        <h4 className="text-sm font-bold text-slate-900 line-clamp-2 leading-tight w-full px-1">
                                            {previewProduct.name}
                                        </h4>

                                        {/* SKU */}
                                        {showSku && previewProduct.sku && (
                                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                                SKU: {previewProduct.sku}
                                            </p>
                                        )}

                                        {/* Barcode SVG */}
                                        <div
                                            className="w-full my-2 flex items-center justify-center overflow-hidden"
                                            dangerouslySetInnerHTML={{
                                                __html: generateBarcodeSvg(
                                                    previewProduct.barcode || previewProduct.sku || previewProduct.id.substring(0, 10),
                                                    activePreset.barcodeHeight * (zoomLevel * 0.9)
                                                )
                                            }}
                                        />

                                        {/* Barcode Numeric Digits */}
                                        {showBarcodeText && (
                                            <p className="font-mono text-[11px] text-slate-700 font-bold tracking-widest leading-none">
                                                {previewProduct.barcode || previewProduct.sku || previewProduct.id.substring(0, 10)}
                                            </p>
                                        )}

                                        {/* Retail Price Banner */}
                                        {showPrice && previewProduct.retailPrice && (
                                            <div className="mt-2 pt-1 border-t border-slate-100 w-full flex items-center justify-center">
                                                <span className="text-base font-black text-emerald-700 tracking-tight">
                                                    ₦{parseFloat(previewProduct.retailPrice).toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                    </motion.div>
                                ) : (
                                    <div className="text-center py-12 px-6">
                                        <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mx-auto mb-3 text-slate-400 border border-slate-200 shadow-sm">
                                            <Barcode className="w-8 h-8 text-emerald-600" />
                                        </div>
                                        <h3 className="text-base font-bold text-slate-800 mb-1">Queue is Empty</h3>
                                        <p className="text-xs text-slate-500 max-w-xs mx-auto mb-4">
                                            Search for products on the left, browse multiple items, or load sample data
                                        </p>
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => setShowMultiSelectModal(true)}
                                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5"
                                                type="button"
                                            >
                                                <Layers size={14} /> Browse Catalog
                                            </button>
                                            <button
                                                onClick={handleLoadDemoProducts}
                                                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors"
                                                type="button"
                                            >
                                                + Sample Data
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Bottom Dimension Ruler Tag */}
                                <div className="absolute bottom-3 text-[10px] font-mono text-slate-500 bg-white/80 border border-slate-200 px-2.5 py-1 rounded-lg pointer-events-none">
                                    <span>Scale: {zoomLevel.toFixed(1)}x</span>
                                    <span className="mx-1.5">•</span>
                                    <span>{activePreset.widthMm}mm (W) × {activePreset.heightMm}mm (H)</span>
                                </div>
                            </div>
                        )}

                        {/* TAB 2: QUEUE & QUANTITIES */}
                        {activeTab === 'queue' && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                                    <div className="flex items-center gap-2">
                                        <Grid className="w-4 h-4 text-emerald-600" />
                                        <h3 className="text-sm font-bold text-slate-800">Print Queue Items</h3>
                                    </div>
                                    <span className="text-xs font-semibold text-slate-500">
                                        {queue.length} product{queue.length === 1 ? '' : 's'} · {totalLabelsCount} stickers
                                    </span>
                                </div>

                                {queue.length === 0 ? (
                                    <div className="text-center py-16 px-4">
                                        <Package className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm font-semibold text-slate-600">No products in queue</p>
                                        <p className="text-xs text-slate-400 mt-1">Use the search box above to add items to print</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
                                        {queue.map((item, idx) => (
                                            <div
                                                key={item.product.id}
                                                className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors group"
                                            >
                                                <div className="flex items-center gap-3.5 min-w-0 flex-1">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 text-slate-500 group-hover:border-emerald-300 group-hover:bg-emerald-50 transition-colors">
                                                        <Tag className="w-4 h-4 text-slate-400 group-hover:text-emerald-600" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-bold text-slate-800 truncate">{item.product.name}</p>
                                                            {idx === previewIndex && (
                                                                <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.2 rounded-full">
                                                                    Active Preview
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-0.5">
                                                            <code className="text-[11px] text-slate-400 font-mono">
                                                                {item.product.barcode || item.product.sku || 'Auto-generated'}
                                                            </code>
                                                            {item.product.retailPrice && (
                                                                <span className="text-xs font-bold text-emerald-600">
                                                                    ₦{parseFloat(item.product.retailPrice).toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Copies Controls */}
                                                <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                                                    <div className="flex items-center border border-slate-200 rounded-xl bg-white shadow-xs overflow-hidden">
                                                        <button
                                                            onClick={() => handleUpdateCopies(item.product.id, -1)}
                                                            className="p-2 hover:bg-slate-100 text-slate-500 transition-colors"
                                                            type="button"
                                                            title="Decrease"
                                                        >
                                                            <Minus size={13} />
                                                        </button>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={500}
                                                            value={item.copies}
                                                            onChange={(e) => handleSetCopiesDirect(item.product.id, parseInt(e.target.value) || 1)}
                                                            className="w-11 text-center text-xs font-bold text-slate-800 border-x border-slate-100 py-1.5 focus:outline-none"
                                                        />
                                                        <button
                                                            onClick={() => handleUpdateCopies(item.product.id, 1)}
                                                            className="p-2 hover:bg-slate-100 text-slate-500 transition-colors"
                                                            type="button"
                                                            title="Increase"
                                                        >
                                                            <Plus size={13} />
                                                        </button>
                                                    </div>

                                                    <button
                                                        onClick={() => {
                                                            setPreviewIndex(idx);
                                                            setActiveTab('studio');
                                                        }}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                        type="button"
                                                        title="View in Studio Canvas"
                                                    >
                                                        <Eye size={16} />
                                                    </button>

                                                    <button
                                                        onClick={() => handleRemoveProduct(item.product.id)}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                        type="button"
                                                        title="Remove from queue"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* BOTTOM ACTION DOCK (CLEAN LIGHT GRADIENT) */}
                        {queue.length > 0 && (
                            <div className="bg-white border-2 border-emerald-200/80 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-bold text-slate-900 text-base">Ready to Print {totalLabelsCount} Labels</h4>
                                        <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                            {activePreset.name}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {activePreset.paperType === 'roll'
                                            ? `Estimated roll length: ~${((activePreset.heightMm * totalLabelsCount) / 1000).toFixed(2)} meters`
                                            : `Requires ${Math.ceil(totalLabelsCount / 24)} A4 sheet(s) (24 labels/sheet)`}
                                    </p>
                                </div>

                                <Button
                                    onClick={handlePrint}
                                    leftIcon={<Printer size={16} />}
                                    className="w-full sm:w-auto px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl shadow-sm"
                                >
                                    Print All {totalLabelsCount} Labels
                                </Button>
                            </div>
                        )}

                    </div>

                </div>
            </div>

            {/* Catalog Multi-Product Selection Modal */}
            {showMultiSelectModal && user?.tenantId && (
                <MultiProductSelectModal
                    isOpen={showMultiSelectModal}
                    onClose={() => setShowMultiSelectModal(false)}
                    onAdd={handleBulkAddProducts}
                    tenantId={user.tenantId}
                />
            )}
        </div>
    );
}

// ============================================================
// CATALOG MULTI-PRODUCT SELECTION MODAL
// ============================================================

interface MultiProductSelectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (items: Array<{ product: ProductResult; copies: number }>) => void;
    tenantId: string;
}

function MultiProductSelectModal({ isOpen, onClose, onAdd, tenantId }: MultiProductSelectModalProps) {
    const [search, setSearch] = useState('');
    const [selectedItems, setSelectedItems] = useState<Record<string, { product: ProductResult; copies: number }>>({});
    const [defaultCopies, setDefaultCopies] = useState(1);

    const { data: products, isLoading } = useQuery({
        queryKey: ['barcode-multi-select-products', tenantId, search],
        queryFn: () => searchProducts(tenantId, search || undefined),
        enabled: isOpen && !!tenantId,
    });

    if (!isOpen) return null;

    const list = products || [];
    const selectedCount = Object.keys(selectedItems).length;
    const totalCopies = Object.values(selectedItems).reduce((sum, item) => sum + item.copies, 0);

    const toggleProduct = (prod: ProductResult) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[prod.id]) {
                delete next[prod.id];
            } else {
                next[prod.id] = { product: prod, copies: defaultCopies };
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedCount === list.length && list.length > 0) {
            setSelectedItems({});
        } else {
            const next: Record<string, { product: ProductResult; copies: number }> = {};
            list.forEach(p => {
                next[p.id] = { product: p, copies: selectedItems[p.id]?.copies || defaultCopies };
            });
            setSelectedItems(next);
        }
    };

    const updateProductCopies = (prodId: string, count: number) => {
        const safe = Math.max(1, Math.min(200, count));
        setSelectedItems(prev => {
            if (!prev[prodId]) return prev;
            return { ...prev, [prodId]: { ...prev[prodId], copies: safe } };
        });
    };

    const setAllCopies = (count: number) => {
        setDefaultCopies(count);
        setSelectedItems(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(k => {
                next[k].copies = count;
            });
            return next;
        });
    };

    const handleConfirm = () => {
        const items = Object.values(selectedItems);
        onAdd(items);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            >
                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700">
                            <Layers className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 text-base">Select Products from Catalog</h3>
                            <p className="text-xs text-slate-500">Pick multiple products to print together on A4 sheets or thermal roll</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-200 text-slate-400">
                        <X size={18} />
                    </button>
                </div>

                {/* Filter and bulk controls */}
                <div className="p-4 border-b border-slate-100 bg-white space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Filter catalog by product name, barcode, or SKU..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                        <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700 select-none">
                            <input
                                type="checkbox"
                                checked={list.length > 0 && selectedCount === list.length}
                                onChange={toggleSelectAll}
                                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                            />
                            Select All Visible ({list.length})
                        </label>
                        <div className="flex items-center gap-2">
                            <span className="text-slate-400 text-[11px]">Default copies:</span>
                            {[1, 2, 4, 6].map(num => (
                                <button
                                    key={num}
                                    type="button"
                                    onClick={() => setAllCopies(num)}
                                    className={clsx(
                                        'px-2 py-0.5 rounded text-[11px] font-bold border transition-colors',
                                        defaultCopies === num ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                                    )}
                                >
                                    {num}x
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Products List */}
                <div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-2">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Spinner size="md" variant="emerald" />
                        </div>
                    ) : list.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 text-xs">
                            No products found matching your search.
                        </div>
                    ) : (
                        list.map(p => {
                            const isChecked = !!selectedItems[p.id];
                            const copies = selectedItems[p.id]?.copies || defaultCopies;
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => toggleProduct(p)}
                                    className={clsx(
                                        'p-3 rounded-xl flex items-center justify-between transition-colors cursor-pointer',
                                        isChecked ? 'bg-emerald-50/60' : 'hover:bg-slate-50'
                                    )}
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleProduct(p)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                        />
                                        <div className="min-w-0">
                                            <p className="text-xs font-bold text-slate-900 truncate">{p.name}</p>
                                            <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                                                <code className="font-mono bg-slate-100 px-1 rounded">{p.barcode || p.sku || 'No Barcode'}</code>
                                                {p.retailPrice && (
                                                    <span className="font-bold text-emerald-700">₦{parseFloat(p.retailPrice).toLocaleString()}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {isChecked && (
                                        <div className="flex items-center gap-2 ml-3" onClick={(e) => e.stopPropagation()}>
                                            <span className="text-[11px] text-slate-500 font-medium">Copies:</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={200}
                                                value={copies}
                                                onChange={(e) => updateProductCopies(p.id, parseInt(e.target.value) || 1)}
                                                className="w-14 px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-center"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="text-xs text-slate-600">
                        <strong className="text-emerald-700 font-bold">{selectedCount}</strong> products selected (
                        <strong className="text-slate-800">{totalCopies}</strong> total labels)
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors"
                        >
                            Cancel
                        </button>
                        <Button
                            onClick={handleConfirm}
                            disabled={selectedCount === 0}
                            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-xl"
                        >
                            Add to Print Queue ({totalCopies})
                        </Button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}